import { useContext, useState } from 'react';

import {
  IconUser,
  IconLock,
  IconShieldCheck,
  IconCheck,
  IconHelpCircle,
  IconPhone,
  IconMail,
  IconLogout,
  IconShieldHalfFilled,
} from '@tabler/icons-react';
import { Auth } from 'aws-amplify';
import { AuthContext, AuthContextAction } from 'context/AuthContext';
import { useFeatureFlag } from 'context/FeatureFlagContext';
import { UserModel } from 'features/pages/account/User';
import { useNavigate } from 'react-router-dom';
import { Avatar } from 'ui-kit';
import { SupportModalV1 } from 'ui-kit/DraggableHelpButton/SupportModalV1';

import { useQuery } from '@apollo/client';

import { GET_USER_PERMISSIONS } from 'query/permissionsQueries';
import { useUsername } from 'utils/authUtils';

import { DropdownMenuItem } from './DropdownMenuItem';

import './UserContextMenu.scss';

const AccountRoutePrefix = '/account';

const menuItems = [
  {
    label: 'User',
    icon: <IconUser size={20} />,
    link: `${AccountRoutePrefix}?section=user`,
  },
  {
    label: 'Security',
    icon: <IconLock size={20} />,
    link: `${AccountRoutePrefix}?section=security`,
  },
  {
    label: 'User Role & Permissions',
    icon: <IconShieldCheck size={20} />,
    link: `${AccountRoutePrefix}?section=permissions`,
    adminOnly: true,
  },
  {
    label: 'Application Preferences',
    icon: <IconCheck size={20} />,
    link: `${AccountRoutePrefix}?section=preferences`,
  },
  {
    label: 'Email Preferences', // New Email Preferences menu item
    icon: <IconMail size={20} />,
    link: `${AccountRoutePrefix}?section=email-preferences`,
  },
  {
    label: 'Support',
    icon: <IconHelpCircle size={20} />,
    link: 'https://orsnn.zendesk.com/hc/en-us',
    openInNewTab: true,
  },
  {
    label: 'Trust Center',
    icon: <IconShieldHalfFilled size={20} />,
    link: 'https://app.vanta.com/orsnn.com/trust/z40h552xusg4m49zkgj57',
    openInNewTab: true,
  },
  {
    label: 'Contact Us',
    icon: <IconPhone size={20} />,
    link: 'https://orsnn.zendesk.com/hc/en-us/requests/new',
    openInNewTab: true,
  },
];

interface UserContextMenuProps {
  userInfo?: UserModel;
}

export const UserContextMenu = ({ userInfo }: UserContextMenuProps) => {
  const { dispatch } = useContext(AuthContext);
  const navigate = useNavigate();
  const [lastLogin, setLastLogin] = useState<string>('');
  const [showSupportModal, setShowSupportModal] = useState(false);
  const hasHelpSupportModal = useFeatureFlag('helpSupportModal');
  const username = useUsername();

  useQuery(GET_USER_PERMISSIONS, {
    variables: { requestData: { username: username || '' } },
    skip: !username,
    onCompleted: (data) => {
      if (data.userManagementGetUser && data.userManagementGetUser.user) {
        setLastLogin(data.userManagementGetUser.user.lastLoginDate);
      }
    },
  });

  return (
    <div className="user_context_container">
      <div className="user-info">
        <Avatar name={`${userInfo?.givenName || ''} ${userInfo?.familyName || ''}`} />
        <div className="user-info-text">
          <span>{`${userInfo?.givenName || ''} ${userInfo?.familyName || ''}`.trim()}</span>
          <span className="email">{userInfo?.email || ''}</span>
          <div className="last-login">
            <span>Latest login</span>
            <span>{lastLogin ? new Date(parseInt(lastLogin, 10)).toLocaleString() : ''}</span>
          </div>
        </div>
      </div>
      <div className="items">
        <hr />
        {menuItems.map((m) => {
          if (m.label === 'Support') {
            return (
              hasHelpSupportModal && (
                <DropdownMenuItem
                  key={m.label}
                  label={m.label}
                  icon={m.icon}
                  onClick={() => setShowSupportModal(true)}
                />
              )
            );
          }

          return (
            <DropdownMenuItem
              key={m.label}
              label={m.label}
              icon={m.icon}
              link={m.link}
              newTab={m.openInNewTab}
            />
          );
        })}
        <hr />
        <DropdownMenuItem
          label="Log Out"
          icon={<IconLogout size={20} />}
          onClick={() => {
            Auth.signOut().then(() => dispatch({ type: AuthContextAction.SIGN_OUT }));
            navigate('/');
          }}
        />
      </div>

      {hasHelpSupportModal && showSupportModal && (
        <SupportModalV1 onClose={() => setShowSupportModal(false)} />
      )}
    </div>
  );
};
